/**
 * Lightweight ET date helpers for initial-route code paths.
 *
 * Avoids pulling full timezone datasets into the startup bundle.
 */

export const PARKING_TZ = 'America/New_York';

const ET_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: PARKING_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const ET_SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: PARKING_TZ,
  month: 'short',
  day: 'numeric',
});
const ET_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: PARKING_TZ,
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (value) => String(value).padStart(2, '0');

const toYmdFromUtcDate = (value) => {
  const year = value.getUTCFullYear();
  const month = pad2(value.getUTCMonth() + 1);
  const day = pad2(value.getUTCDate());
  return `${year}-${month}-${day}`;
};

const toYmdInEt = (value) => {
  const parts = ET_DATE_FORMATTER.formatToParts(value);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
};

const parseDateInput = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (DATE_ONLY_PATTERN.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map((part) => Number(part));
      return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    }
    const parsedString = new Date(trimmed);
    return Number.isNaN(parsedString.getTime()) ? null : parsedString;
  }

  if (value instanceof Date) {
    const parsedDate = new Date(value.getTime());
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatShortDateFromYmd = (ymdDate) => {
  const [year, month, day] = ymdDate.split('-').map((part) => Number(part));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return '';
  }
  const monthName = MONTH_NAMES_SHORT[month - 1];
  if (!monthName) return '';
  return `${monthName} ${day}`;
};

export function todayEST() {
  return toYmdInEt(new Date());
}

export function addDays(baseDate, days = 0) {
  const safeDays = Number.isFinite(Number(days)) ? Number(days) : 0;

  if (typeof baseDate === 'string') {
    const trimmed = baseDate.trim();
    if (DATE_ONLY_PATTERN.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map((value) => Number(value));
      const utcDate = new Date(Date.UTC(year, month - 1, day));
      utcDate.setUTCDate(utcDate.getUTCDate() + safeDays);
      return toYmdFromUtcDate(utcDate);
    }
  }

  const parsed = baseDate instanceof Date
    ? new Date(baseDate.getTime())
    : new Date(baseDate);

  if (Number.isNaN(parsed.getTime())) {
    return todayEST();
  }

  parsed.setUTCDate(parsed.getUTCDate() + safeDays);
  return toYmdInEt(parsed);
}

export function formatShortDate(dateValue) {
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    if (DATE_ONLY_PATTERN.test(trimmed)) {
      return formatShortDateFromYmd(trimmed);
    }
  }

  const parsed = parseDateInput(dateValue);
  if (!parsed) return '';
  return ET_SHORT_DATE_FORMATTER.format(parsed);
}

export function formatDateTime(dateValue) {
  const parsed = parseDateInput(dateValue);
  if (!parsed) return '';
  return ET_DATE_TIME_FORMATTER.format(parsed).replace(/, (?=\d{1,2}:\d{2} [AP]M$)/, ' ');
}
